import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Panel } from '../../../components/Panel'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { TileCard } from './DeskToday'
import { GroupNote } from './GroupNote'
import { NOTE_GOOGLE } from '../../../lib/deskGroupNote'
import { useDeskState } from './useDeskState'
import { deskApi, type BrandSide, type DeskPub, type SearchRow, type SearchView } from '../../../api/visitorsDesk'
import { clicksChange, coverageText, positionChange, rowPositionChange, siteResultsText, VIEW_BUTTONS } from '../../../lib/deskSearch'
import { fmtDay, pubLabel } from '../../../lib/deskFormat'
import { fmtInt } from '../../../lib/format'

const TONE = { up: 'text-radar-cyan', down: 'text-amber-glow', flat: 'text-paper-faint' } as const
const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
const td = 'px-2 py-1.5 align-top'
const mono = { fontVariantNumeric: 'tabular-nums' } as const
const pct = (n: number | null) => (n === null ? '—' : `${(n * 100).toFixed(n < 0.1 ? 1 : 0)}%`)

function Loading() { return <div className="h-24 bg-ink-800 animate-pulse" /> }
function Failed({ what }: { what: string }) { return <p className="font-mono text-[12px] text-radar-red">Couldn’t load {what}</p> }

export function DeskSearch() {
  return <DeskFrame tab="search"><Body /></DeskFrame>
}

type Q = ReturnType<typeof useDeskState>['q']

function Body() {
  const { pub, q, sp, spKey, setSp } = useDeskState('search')
  const p = pub as DeskPub
  const key = [pub, q.period, q.from, q.to, q.compare, q.group]
  const head = useQuery({ queryKey: ['desk', 'search', ...key], queryFn: () => deskApi.search(p, q), retry: false })
  const urlView = sp.get('view')
  const button = VIEW_BUTTONS.find((b) => b.views.includes(urlView as SearchView)) ?? VIEW_BUTTONS[0]
  const pick = (id: string) => {
    const b = VIEW_BUTTONS.find((x) => x.id === id) ?? VIEW_BUTTONS[0]
    const next = new URLSearchParams(spKey)
    next.set('view', b.views[0])
    next.delete('q')
    setSp(next)
  }
  const days = head.data?.meta.period?.days
  const prevLabel = days ? `prev ${days}d` : 'prev period'

  if (head.isError) return <Failed what="Search" />
  if (!head.data) return <Loading />
  const d = head.data
  const unavailable = d.unavailable !== undefined
  return (
    <div className="space-y-4">
      <GroupNote applied={d.group_applied} text={NOTE_GOOGLE} />
      {unavailable ? <NotYetLive text={d.unavailable} /> : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="border border-ink-500 px-2 py-1 font-mono text-[12px] text-paper-dim">
              {d.behind_text}{d.data_through ? ` · data through ${fmtDay(d.data_through, { month: 'short', day: 'numeric' })}` : ''}
            </span>
          </div>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
            {d.tiles.map((t) => (
              <TileCard key={t.key} name={t.label} compared={t.compared} prevLabel={prevLabel} unit={t.key === 'position' ? 'positions' : undefined}
                        sub={t.key === 'position' ? 'lower is better' : null} context="from Search Console" />
            ))}
          </div>
          <Terms p={p} q={q} pub={pub} keyBase={key} button={button.id} onPick={pick} focus={sp.get('q')} />
          <Competing p={p} q={q} keyBase={key} pub={pub} />
        </>
      )}
      <SiteSearches p={p} q={q} keyBase={key} pub={pub} />
    </div>
  )
}

function Terms({ p, q, pub, keyBase, button, onPick, focus }: {
  p: DeskPub; q: Q; pub: string; keyBase: unknown[]; button: string; onPick: (id: string) => void; focus: string | null
}) {
  const b = VIEW_BUTTONS.find((x) => x.id === button) ?? VIEW_BUTTONS[0]
  return (
    <Panel label="Search terms people used to find us" accent="amber">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Search term views">
          {VIEW_BUTTONS.map((x) => (
            <button key={x.id} type="button" aria-pressed={x.id === b.id} onClick={() => onPick(x.id)}
                    className={`min-h-[40px] border px-3 text-[13px] ${x.id === b.id ? 'border-amber-glow bg-ink-700 text-paper' : 'border-ink-500 text-paper-dim hover:text-paper'}`}>{x.label}</button>
          ))}
        </div>
        {b.id === 'brand' && <BrandPanel p={p} q={q} keyBase={keyBase} />}
        {b.views.map((v) => <TermView key={v} p={p} q={q} pub={pub} keyBase={keyBase} view={v} heading={b.views.length > 1 ? VIEW_TITLES[v] : null} focus={focus} />)}
      </div>
    </Panel>
  )
}

const VIEW_TITLES: Record<SearchView, string> = {
  close: 'Close to page one', ctr_gap: 'Ranked, not clicked', rising: 'Rising', falling: 'Falling',
  brand: 'Brand searches', topic: 'Topic searches', discover: 'Google Discover', news: 'Google News',
}

function TermView({ p, q, pub, keyBase, view, heading, focus }: {
  p: DeskPub; q: Q; pub: string; keyBase: unknown[]; view: SearchView; heading: string | null; focus: string | null
}) {
  const query = useQuery({ queryKey: ['desk', 'search-terms', ...keyBase, view], queryFn: () => deskApi.searchTerms(p, q, view), retry: false })
  const d = query.data
  if (query.isError) return <Failed what="search terms" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <p className="text-paper">{d.unavailable}</p>
  const clicksView = view === 'rising' || view === 'falling'
  const cov = coverageText(d.coverage.visible_share)
  const feed = view === 'discover' || view === 'news'
  const rows = focus ? d.rows.filter((r) => (r.query ?? '').toLowerCase().includes(focus.toLowerCase())) : d.rows
  return (
    <div className="space-y-2">
      {heading && <h4 className="font-mono text-[12px] font-semibold uppercase tracking-wide text-paper-dim">{heading}</h4>}
      <p className="text-[13px] text-paper-dim">{d.note}</p>
      {focus && <p className="font-mono text-[12px] text-paper-faint">Showing terms containing “{focus}”.</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-[13px]">
          <thead>
            <tr>
              <th className={th}>Search term</th><th className={th}>Our page Google shows</th>
              {['Shown', 'Clicks', 'Click rate', 'Expected at this rank', 'Position', 'Change'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
              <th className={th}>Suggested move</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: SearchRow) => {
              const ch = clicksView ? clicksChange(r.change) : positionChange({ change: rowPositionChange(r) })
              return (
                <tr key={`${r.publication}-${r.query}-${r.page_key}`} className="border-t border-ink-600">
                  <td className={td}>
                    <span className="block max-w-[240px] truncate font-semibold" title={r.query ?? ''}>{r.query ?? (feed ? `(${VIEW_TITLES[view].replace('Google ', '')})` : '—')}</span>
                    {pub === 'all' && <span className="font-mono text-[11px] text-paper-faint">{pubLabel(r.publication)}</span>}
                  </td>
                  <td className={td}><span className="block max-w-[260px] truncate" title={r.page_url ?? ''}>{r.page_url ?? '—'}</span></td>
                  <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.impressions)}</td>
                  <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.clicks)}</td>
                  <td className={`${td} text-right font-mono`} style={mono}>{pct(r.ctr)}</td>
                  <td className={`${td} text-right font-mono`} style={mono}>{pct(r.expected_ctr)}</td>
                  <td className={`${td} text-right font-mono`} style={mono}>{r.position === null ? '—' : r.position.toFixed(1)}</td>
                  <td className={`${td} text-right font-mono ${TONE[ch.tone]}`} style={mono}>{ch.text}</td>
                  <td className={td}>{r.move ? <span className="bg-ink-700 px-1.5 py-0.5 text-[12px] text-radar-green">{r.move}</span> : <span className="text-paper-faint">—</span>}</td>
                </tr>
              )
            })}
            {rows.length === 0 && <tr><td colSpan={9} className={`${td} text-paper-faint`}>Nothing in this view for this period</td></tr>}
          </tbody>
        </table>
      </div>
      {cov && <p className="text-[12px] text-paper-faint">{cov}</p>}
    </div>
  )
}

function BrandPanel({ p, q, keyBase }: { p: DeskPub; q: Q; keyBase: unknown[] }) {
  const query = useQuery({ queryKey: ['desk', 'search-brand', ...keyBase], queryFn: () => deskApi.searchBrand(p, q), retry: false })
  const d = query.data
  if (query.isError) return <Failed what="brand and topic totals" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <p className="text-paper">{d.unavailable}</p>
  const sides: [string, BrandSide | null][] = [['Brand', d.brand], ['Topic', d.topic], ['Hidden by Google', d.anonymised]]
  const total = sides.reduce((a, [, s]) => a + (s?.clicks ?? 0), 0)
  const words = Object.entries(d.terms).flatMap(([pubSlug, t]) => (t.length ? [`${pubLabel(pubSlug)}: ${t.join(', ')}`] : []))
  return (
    <div className="space-y-2 border border-ink-600 p-3">
      <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
        {sides.map(([name, s]) => (
          <div key={name} className="min-w-0">
            <div className="label">{name}</div>
            {s ? (
              <>
                <div className="font-mono text-[20px] font-semibold" style={mono}>{fmtInt(s.clicks)} <span className="text-[12px] font-normal text-paper-faint">clicks{total > 0 && total >= 30 ? ` (${Math.round((s.clicks / total) * 100)}%)` : ''}</span></div>
                <div className="font-mono text-[12px] text-paper-faint" style={mono}>{fmtInt(s.impressions)} shown · {fmtInt(s.queries)} terms</div>
              </>
            ) : <div className="font-mono text-[13px] text-paper-faint">—</div>}
          </div>
        ))}
      </div>
      {words.length > 0 && <p className="text-[12px] text-paper-faint">Brand words: {words.join(' · ')}</p>}
    </div>
  )
}

function Competing({ p, q, pub, keyBase }: { p: DeskPub; q: Q; pub: string; keyBase: unknown[] }) {
  const query = useQuery({ queryKey: ['desk', 'search-competing', ...keyBase], queryFn: () => deskApi.searchCompeting(p, q), retry: false })
  const d = query.data
  return (
    <Panel label="Two of our pages competing for one search" accent="violet">
      {query.isError ? <Failed what="competing pages" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-[13px]">
              <thead><tr><th className={th}>Search term</th><th className={th}>Pages Google alternates between</th><th className={`${th} text-right`}>Clicks</th><th className={th}>Suggested move</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={`${r.publication}-${r.query}`} className="border-t border-ink-600">
                    <td className={td}>
                      <span className="block max-w-[220px] truncate font-semibold" title={r.query}>{r.query}</span>
                      {pub === 'all' && <span className="font-mono text-[11px] text-paper-faint">{pubLabel(r.publication)}</span>}
                    </td>
                    <td className={td}>
                      {r.pages.map((pg) => <span key={pg.page_key ?? pg.page_url} className="block max-w-[360px] truncate" title={pg.page_url ?? ''}>{pg.page_url ?? '—'}</span>)}
                    </td>
                    <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.clicks)}</td>
                    <td className={td}>{r.move ? <span className="bg-ink-700 px-1.5 py-0.5 text-[12px] text-radar-green">{r.move}</span> : '—'}</td>
                  </tr>
                ))}
                {d.rows.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No searches where two of our pages split the impressions</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-paper-faint">Fix by linking the weaker page to the stronger one, or merging them.</p>
        </div>
      )}
    </Panel>
  )
}

function SiteSearches({ p, q, pub, keyBase }: { p: DeskPub; q: Q; pub: string; keyBase: unknown[] }) {
  const [zeroOnly, setZeroOnly] = useState(false)
  const query = useQuery({
    queryKey: ['desk', 'search-site', ...keyBase, zeroOnly],
    queryFn: () => deskApi.searchSite(p, q, zeroOnly ? { zero_only: true } : undefined), retry: false,
  })
  const d = query.data
  return (
    <Panel label="Searches on our own site" accent="cyan"
           right={<label className="flex min-h-[32px] items-center gap-2 font-mono text-[12px] text-paper-dim"><input type="checkbox" checked={zeroOnly} onChange={(e) => setZeroOnly(e.target.checked)} />Zero results only</label>}>
      {query.isError ? <Failed what="site searches" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-[13px]">
              <thead><tr><th className={th}>Typed in our search box</th><th className={th}>Where</th><th className={`${th} text-right`}>Times</th><th className={th}>Results</th><th className={th}>Clicked next</th></tr></thead>
              <tbody>
                {d.rows.map((r) => {
                  const res = siteResultsText(r)
                  return (
                    <tr key={`${r.publication}-${r.surface}-${r.q}`} className="border-t border-ink-600">
                      <td className={td}>
                        <span className="block max-w-[240px] truncate font-semibold" title={r.label}>{r.label}</span>
                        {pub === 'all' && <span className="font-mono text-[11px] text-paper-faint">{pubLabel(r.publication)}</span>}
                      </td>
                      <td className={`${td} text-paper-faint`}>{r.surface}</td>
                      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.searches)}</td>
                      <td className={`${td} font-mono ${res.zero ? 'font-semibold text-amber-glow' : ''}`} style={mono}>{res.text}</td>
                      <td className={td}><span className="block max-w-[260px] truncate" title={r.next_path ?? ''}>{r.next_path ?? 'left the site'}</span></td>
                    </tr>
                  )
                })}
                {d.rows.length === 0 && <tr><td colSpan={5} className={`${td} text-paper-faint`}>{zeroOnly ? 'No zero-result searches in this period' : 'Nobody used our search box in this period'}</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[12px] text-paper-faint">Zero-result searches are things readers want that we don't have or can't find. They're story ideas, and an archive-tagging to-do list. Topic searches show “4+” because the page only counts up to 4 results.</p>
        </div>
      )}
    </Panel>
  )
}
